import React, { FC, useState, useContext, useMemo, useEffect } from 'react';
import Typography from '@pixel/core/Typography';
import Paper from '@pixel/core/Paper';
import DataTable from '@pixel/core/DataTable';
import Link from '@pixel/core/Link';
import ListBase, { ListSearchChangeProps } from '@pixel/core/ListBase';
import Tooltip from '@pixel/core/Tooltip';
import LayoutRootSection from '@pixel/internal/LayoutRootSection';
import CircularProgress from '@pixel/core/CircularProgress';
import { SelectChangeEvent } from '@mui/material/Select';
import { useMultipleLocalizedText } from '../../../contexts/Localization/localizationHooks';
import TableChartIcon from '@pixel/assets/icons/TableChart';
import { IUsersGetParms, IUsersData, IUser, IUsersSearchGetParms, IAccessGroupsData, IDimensionsAPIPayload } from '../OdsTypes';
import Box from '@pixel/core/Box';
import Chip from "@pixel/core/Chip";
import OdsContext from '../OdsContext';
import { transformUsersResult } from '../OdsTransform';
import { useConfirmation, DialogLabelSet } from '@pixel/core/ConfirmationService';
import { useSnackbar } from '@pixel/core/SnackbarService';
import axios from 'axios';

interface UsersListProps {
  selectedUserData: IAccessGroupsData;
  usersListForRemoval: IDimensionsAPIPayload[];
  setUsersListForRemoval: React.Dispatch<React.SetStateAction<IDimensionsAPIPayload[]>>;
  addEmployees: any;
  setAddEmployees: any;
  setAddAccessGroupPayload: any;
  addAccessGroupPayload: any;
}

const UsersList: FC<UsersListProps> = ({
  selectedUserData,
  usersListForRemoval, 
  setUsersListForRemoval,
  addEmployees,
  setAddEmployees,
  setAddAccessGroupPayload,
  addAccessGroupPayload,
}) => {
  const jvUsersTitle = {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    wordBreak: 'break-all',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  }

  const overlayStyling = {
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    alignItems: 'center',
    zIndex: '1',
  }

  const {
    COMMON_NAME,
    ODS_ALL,
    ODS_SEARCH_PLACEHOLDER,
    ACCESS_SEARCH_USERS,
    ACCESS_GROUP_OTHER,
    ODS_STATUS,
    ODS_ROWS_PER_PAGE_LABEL,
    ACCESS_GROUP_ROLES,
    I18N_NO_RESULTS_FOUND,
    ACCESS_GROUP_REMOVE,
    ACCESS_GROUP_SELECTED,
    EMPLOYEE_ACCESS_GROUP_CODE,

  } = useMultipleLocalizedText();

  const confirm = useConfirmation();
  const OrgElementsContext = useContext(OdsContext);
  const ODSPageSizeSessionStorage = () =>
    JSON.parse(sessionStorage.getItem('accessGroup_pageSize'))
      ? JSON.parse(sessionStorage.getItem('accessGroup_pageSize'))
      : OrgElementsContext.defaultPageSize;

  const UsersFetchParams: IUsersGetParms = useMemo(
    () => ({
      pageSize: ODSPageSizeSessionStorage(),
      pageNumber: 0,
    }),
    []
  );

  const UsersSearchParams: IUsersSearchGetParms = useMemo(
    () => ({
      pageSize: ODSPageSizeSessionStorage(),
      pageNumber: 0,
      keywords: '',
    }),
    []
  );

  const snack = useSnackbar();
  const [listLoading, setListLoading] = useState(false);
  const [usersFetchPayload, setUsersFetchPayload] = useState<IUsersGetParms>(UsersFetchParams);
  const [usersSearchPayload, setUsersSearchPayload] = useState<IUsersSearchGetParms>(UsersSearchParams);
  const [results, setResults] = useState<IUsersData>({ nodes: [], totalCount: 0, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
  const [perPageSize, setPerPageSize] = useState(ODSPageSizeSessionStorage());
  const [noSearchResult, setNoSearchResult] = useState(false);
  const [searchText, setSearchText] = useState(null);
  const [showInProgress, setShowInProgress] = useState(false);
  const [isDefaultAccessGroup, setIsDefaultAccessGroup] = useState(false);
  const [pagingContext, setPagingContext] = useState({
    hideFirstLastActions: true,
    perPage: perPageSize,
    page: 1,
    first: 1,
    last: perPageSize,
  });



  const fetchUsers = async (usersFetchPayload: IUsersGetParms) => {
    try {
      setListLoading(true);
      let userEid = selectedUserData ? selectedUserData.eid : '';
      if(userEid !== ''){
      await axios
        .get(window.jvUrls.admin + '/action/accessGroups/users/' + userEid, {
          params: usersFetchPayload,
        })
        .then(function (response) {
          setResults(transformUsersResult(response.data));
          response.data.users.length > 0 && setNoSearchResult(false);
          setPagingContext({
            hideFirstLastActions: true,
            perPage: response.data.pageSize,
            page: response.data.pageNumber + 1,
            first:
              response.data.users.length > 0
                ? response.data.pageNumber * response.data.pageSize + 1
                : 0,
            last:
              response.data.pageSize * response.data.pageNumber +
              response.data.users.length,
          });
          setListLoading(false);
        })
        .catch(function (error) {
          setNoSearchResult(true);
          console.log(error);
          setListLoading(false);
        });
      } else {
        setListLoading(false);
        setNoSearchResult(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchUsers(usersFetchPayload);
  }, [usersFetchPayload]);

  const onPaginationChange = (event: SelectChangeEvent) => {
    sessionStorage.setItem('accessGroup_pageSize', JSON.stringify(Number(event.target.value)));
    setPerPageSize(Number(event.target.value));
    if (searchText && searchText !== '') {
      setUsersSearchPayload({ ...usersSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: searchText })
      setListLoading(true);
    } else {
      setUsersFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
    }
  }

  const onPageChanged = (event: any, currPage: number) => {
    if (searchText && searchText.length > 1) {
      setUsersSearchPayload({ ...usersSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1 });
    } else {
      setUsersFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1 });
    }
  }

  useEffect(() => {
    if (searchText && searchText.length > 1) {
      let userEid = selectedUserData ? selectedUserData.eid : '';
      axios.get(window.jvUrls.admin + '/action/accessGroups/users/' + userEid, {
        params: usersSearchPayload
      })
        .then(function (response) {
          setResults(transformUsersResult(response.data));
          setPagingContext({
            hideFirstLastActions: true,
            perPage: response.data.pageSize,
            page: response.data.pageNumber + 1,
            first:
              response.data.users.length > 0
                ? response.data.pageNumber * response.data.pageSize + 1
                : 0,
            last:
              response.data.pageSize * response.data.pageNumber +
              response.data.users.length,
          });
          setListLoading(false);
          response.data.totalCount == 0 ? setNoSearchResult(true) : setNoSearchResult(false);
        })
        .catch(function (error) {
          console.log(error);
          setListLoading(false);
        });
    }
  }, [searchText, usersSearchPayload]);

  const resetSearch = () => {
    if (searchText !== '') {
      setUsersFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
    }
  }

  const handleSearch = (props: ListSearchChangeProps) => {
    setSearchText(props.q);
    if (props.q.length > 1) {
      setUsersSearchPayload({ ...usersSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: props.q })
      setListLoading(true);
    } else {
      setNoSearchResult(false);
      resetSearch();
    }
  };

  const onRemoveUser = (node: IUser) => {
    setUsersListForRemoval([...usersListForRemoval, node.eid]);
    setAddAccessGroupPayload({...addAccessGroupPayload, accessGroupUserSelectionList: [...addAccessGroupPayload.accessGroupUserSelectionList, {eid: node.eid, selected: false}]});
  }

  const populateActionsLinks = (node: IUser) => {
    if(usersListForRemoval.includes(node.eid)){
      return <Typography className='remove-user-text' variant='body2' >{ACCESS_GROUP_SELECTED}</Typography>
    } else {
        if (selectedUserData.code == EMPLOYEE_ACCESS_GROUP_CODE) {
            return <Link underline="hover"  className={'remove-user-link-grayOut'}  variant="body2">{ACCESS_GROUP_REMOVE}</Link>
        } else {
            return <Link underline="hover"  className={'remove-user-link'} onClick={() => onRemoveUser(node)} variant="body2">{ACCESS_GROUP_REMOVE}</Link>
        }
    }
  }

  const populateRoles = (node: any) => {
    let otherRoles: Array<string> = [];
    const otherRoleLength = node.roles.length - 2;
    return node.roles?.map((role: string, index: number) => {
      const roleName = role;
      if (index <= 1) {
        return <React.Fragment>{index > 0 && ', '}<Tooltip arrow title={roleName} placement='bottom'><span><Typography className='jv-role-name' sx={{maxWidth: '110px', lineHeight: '18px', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block'}} variant='body2' >{roleName}</Typography></span></Tooltip></React.Fragment>;
      } else {
        otherRoles = [...otherRoles, roleName];
        if(index == node.roles.length-1){
          return <React.Fragment><Box className='jv-roles-more-items'><Typography className='jv-other-role' sx={{display: 'inline-block', padding: '0 5px'}} variant='body2' >and</Typography> <Tooltip arrow title={otherRoles.join(', ')} placement='bottom'><span style={{color:'#1872CC'}}>{otherRoleLength} Other</span></Tooltip></Box></React.Fragment>; 
        }
      }
    })
  }

  const populateOtherGroups = (node: any) => {
    let otherGroups: Array<string> = [];
    const otherGroupLength = node.otherAccessGroups.length - 2;
    return node.otherAccessGroups?.map((group: string, index: number) => {
      if (index <= 1) {
      return <React.Fragment><Tooltip arrow title={group} placement='bottom'><span><Chip label={group} sx={{ ml: 1, height: '26px', maxWidth: '140px', verticalAlign: 'text-bottom' }} /></span></Tooltip></React.Fragment>
      } else {
        otherGroups = [...otherGroups, group];
        if(index == node.otherAccessGroups.length-1){
          return <React.Fragment><Box className='jv-goups-more-items'><Typography className='jv-other-group' sx={{display: 'inline-block', padding: '0 5px'}} variant='body2' >and</Typography> <Tooltip arrow title={otherGroups.join(', ')} placement='bottom'><span style={{color:'#1872CC'}}>{otherGroupLength} Other</span></Tooltip></Box></React.Fragment>; 
        }
      }
    })
  }

  return (
    <LayoutRootSection top bottom transparent className='jv-users-list-wrapper'>
      {(showInProgress && <Box sx={overlayStyling}><CircularProgress size={60} /></Box>)}
      <ListBase
        className='users-list-base'
        showSearch
        labelSearch={ODS_SEARCH_PLACEHOLDER}
        labelSearchAria={ACCESS_SEARCH_USERS}
        // @ts-ignore
        results={results}
        onSearch={handleSearch}
        appliedSearchQuery={searchText}
        pagingContext={pagingContext}
        onPageChanged={onPageChanged}
        onPaginationChange={(event: SelectChangeEvent) => onPaginationChange(event)}
        rowsPerPageLabel={ODS_ROWS_PER_PAGE_LABEL}
        resultsPerPage={perPageSize}
        perPageOptions={OrgElementsContext.perPageSelectOptions}
        viewContexts={[
          {
            ariaLabel: 'Datatable View',
            icon: <TableChartIcon />,
            renderResults: (viewContextProps) => {
              if (listLoading) {
                return (<div className='users-list-loader' style={{ display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={34} />
                </div>)
              } else {
                if (!noSearchResult && results.totalCount !== 0) {
                  return (<DataTable
                    {...viewContextProps}
                    getRowID={node => node.id}
                    columns={[
                      { id: 'Name', columnName: COMMON_NAME, minWidth: 100, maxWidth: 120, renderCell: (node: IUser) => <Tooltip title={node.name} arrow placement='bottom'><span><Typography variant='body2' sx={{ ...jvUsersTitle, maxWidth: '100%' }}>{node.name}</Typography></span></Tooltip> },
                      { id: 'AccessGroups', columnName: ACCESS_GROUP_OTHER, renderCell: (node: IUser) => populateOtherGroups(node), minWidth: 120, maxWidth: 200 },
                      { id: 'Roles', columnName: ACCESS_GROUP_ROLES, minWidth: 160, maxWidth: 180, renderCell: (node:any) => populateRoles(node)},
                      { id: 'Action', columnName: ACCESS_GROUP_REMOVE, renderCell: (node: IUser) => populateActionsLinks(node), minWidth: 50, maxWidth: 60 },
                    ]}
                  />)
                } else {
                  return (<div className={'users-list-no-results'}><Paper sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}> {I18N_NO_RESULTS_FOUND}</Paper></div>)
                }
              }
            }
          }
        ]}
      />
    </LayoutRootSection>
  );
}

export default UsersList;